import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  PermissionLite,
  PermissionsByModule,
  useGetAllPermissionsQuery,
  useGetRoleDetailsQuery,
  useRevokeAllRolePermissionsMutation,
  useSyncRolePermissionsMutation,
} from "../api/accessApi";

function titleCase(s: string) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function buildNameSet(names: string[]) {
  const map: Record<string, true> = {};
  names.forEach((n) => {
    const k = String(n || "").trim();
    if (k) map[k] = true;
  });
  return map;
}

function getErrMsg(err: any) {
  return err?.data?.message || err?.error || err?.status || "Request failed";
}

export default function RoleDetailsPage() {
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const id = Number(idParam);

  const {
    data: role,
    isLoading: roleLoading,
    error: roleError,
    refetch: refetchRole,
  } = useGetRoleDetailsQuery(id, { skip: !id });

  const [localErr, setLocalErr] = useState<string | null>(null);
  const [localOk, setLocalOk] = useState<string | null>(null);

  const {
    data: permsByModule,
    isLoading: permsLoading,
    error: permsError,
    refetch: refetchPerms,
  } = useGetAllPermissionsQuery();

  const [syncPerms, { isLoading: saving }] = useSyncRolePermissionsMutation();
  const [revokeAll, { isLoading: revoking }] =
    useRevokeAllRolePermissionsMutation();

  // ✅ selected permission NAMES (backend expects strings)
  const [selectedNames, setSelectedNames] = useState<string[]>([]);

  useEffect(() => {
    const current = (role?.permissions || []).map((p: any) => String(p.name));
    setSelectedNames(current);
  }, [role?.id]);

  const selectedSet = useMemo(
    () => buildNameSet(selectedNames),
    [selectedNames]
  );

  const togglePermission = (permName: string) => {
    setSelectedNames((prev) => {
      if (prev.includes(permName)) return prev.filter((x) => x !== permName);
      return [...prev, permName];
    });
  };

  const save = async () => {
    setLocalErr(null);
    setLocalOk(null);

    try {
      await syncPerms({ roleId: id, permissions: selectedNames }).unwrap();
      setLocalOk("Permissions updated successfully.");
      refetchRole();
    } catch (e: any) {
      setLocalErr(getErrMsg(e));
      console.error("SYNC PERMS ERROR", e);
    }
  };

  const revokeAllNow = async () => {
    const ok = window.confirm(
      `Revoke ALL permissions for "${role?.name ?? "this role"}"?`
    );
    if (!ok) return;

    setLocalErr(null);
    setLocalOk(null);

    try {
      await revokeAll(id).unwrap();
      setSelectedNames([]);
      setLocalOk("All permissions revoked.");
      refetchRole();
    } catch (e: any) {
      setLocalErr(getErrMsg(e));
      console.error("REVOKE ERROR", e);
    }
  };

  const modules: [string, PermissionLite[]][] = useMemo(() => {
    const obj: PermissionsByModule = permsByModule || {};
    return Object.entries(obj).sort(([a], [b]) => a.localeCompare(b));
  }, [permsByModule]);

  const loading = roleLoading || permsLoading;
  const error = roleError || permsError;

  return (
    <div className="bg-white border rounded-xl p-6">
      {localErr ? (
        <div className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
          {localErr}
        </div>
      ) : null}

      {localOk ? (
        <div className="mt-4 p-3 rounded-lg border border-green-200 bg-green-50 text-green-700 text-sm">
          {localOk}
        </div>
      ) : null}

      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            onClick={() => navigate("/access/roles")}
            className="text-sm text-gray-600 hover:underline"
          >
            ← Back to Roles
          </button>

          <h1 className="text-xl font-bold text-gray-800 mt-2">
            Role Permissions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Tick privileges to assign to this role, then save.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              refetchRole();
              refetchPerms();
            }}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            Refresh
          </button>

          <button
            onClick={revokeAllNow}
            disabled={revoking || !id}
            className="px-4 py-2 rounded-lg border text-red-700 hover:bg-red-50 text-sm disabled:opacity-60"
          >
            {revoking ? "Revoking..." : "Revoke all"}
          </button>

          <button
            onClick={save}
            disabled={saving || !id}
            className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border rounded-xl p-4">
          <div className="text-xs text-gray-500">Role</div>
          <div className="font-semibold text-gray-900 mt-1">
            {role?.name ?? "—"}
          </div>
        </div>
        <div className="border rounded-xl p-4">
          <div className="text-xs text-gray-500">Guard</div>
          <div className="font-semibold text-gray-900 mt-1">
            {role?.guard_name ?? "—"}
          </div>
        </div>
        <div className="border rounded-xl p-4">
          <div className="text-xs text-gray-500">Selected</div>
          <div className="font-semibold text-gray-900 mt-1">
            {selectedNames.length}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 text-gray-600">Loading permissions...</div>
      ) : error ? (
        <div className="mt-6 text-red-600">
          Failed to load data. Check your API / token.
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {modules.map(([moduleName, perms]) => {
            const names = perms.map((p) => p.name);
            const selectedCount = perms.filter(
              (p) => !!selectedSet[p.name]
            ).length;

            const selectAll = () => {
              setSelectedNames((prev) => {
                const merged = [...prev];
                names.forEach((n) => {
                  if (!merged.includes(n)) merged.push(n);
                });
                return merged;
              });
            };

            const clearAll = () => {
              setSelectedNames((prev) =>
                prev.filter((x) => !names.includes(x))
              );
            };

            return (
              <div key={moduleName} className="border rounded-xl p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="font-semibold text-gray-800">
                      {titleCase(moduleName)}
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedCount} of {perms.length} selected
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={selectAll}
                      className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                    >
                      Select all
                    </button>
                    <button
                      onClick={clearAll}
                      className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {perms
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((p) => {
                      const checked = !!selectedSet[p.name];

                      return (
                        <label
                          key={p.id}
                          className={[
                            "flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer",
                            "hover:bg-gray-50",
                            checked ? "bg-gray-50" : "",
                          ].join(" ")}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePermission(p.name)}
                            className="h-4 w-4"
                          />
                          <div className="min-w-0">
                            <div className="text-sm text-gray-800 truncate">
                              {p.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              action: {p.action}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                </div>
              </div>
            );
          })}

          {!modules.length ? (
            <div className="text-gray-500">No permissions available.</div>
          ) : null}
        </div>
      )}
    </div>
  );
}
